"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import AgeIcon from "@/assets/icons/age.svg";
import EyeIcon from "@/assets/icons/eye.svg";
import WellnessLeafIcon from "@/assets/icons/wellness_leaf.svg";

type ComparisonCase = {
  readonly before: string;
  readonly after: string;
};

/** Warm the HTTP cache and decode every before/after pair before the first tab swap. */
const preloadComparisonCaseImages = (cases: ReadonlyArray<ComparisonCase>): void => {
  for (const { before, after } of cases) {
    for (const url of [before, after]) {
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      if (typeof img.decode === "function") {
        void img.decode().catch(() => {});
      }
    }
  }
};

type TabDef = {
  readonly id: string;
  readonly label: string;
};

/*
 * Each tab id maps to the brand icon that visualises that comparison
 * pillar. Keep this aligned with the `tabs` array constructed in
 * InteractiveDemoSection.tsx — if a new tab id is introduced there
 * without a matching entry here, the tab will render without an icon
 * (intentional: better visible gap than a confusing fallback glyph).
 */
const TAB_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  aesthetics: EyeIcon,
  wellness: WellnessLeafIcon,
  futureAging: AgeIcon,
};

/** Full 0↔100 sweeps on one case before the gallery advances to the next tab. */
const AUTO_SWOOPS_PER_SLIDE = 4;
const AUTO_SWOOP_DURATION_MS = 2200;
/** Matches auto-loop length so the tab progress dial and slide swap stay in sync. */
const AUTO_TAB_CYCLE_MS = AUTO_SWOOPS_PER_SLIDE * AUTO_SWOOP_DURATION_MS;
/** Fast crossfade when the active comparison pair changes. */
const SLIDE_FADE_MS = 260;
/** Idle delay after the phone reveal finishes before auto-play starts. */
const AUTO_START_DELAY_MS = 1200;
/** Matches `.alt-hero__phone-wrap.is-revealed .phone` transition in prelaunch.css. */
const PHONE_REVEAL_TRANSITION_MS = 900;
/** Fraction of the phone wrap that must be visible before reveal / auto-play. */
const PHONE_IN_VIEW_THRESHOLD = 0.4;

type PhoneComparisonSliderProps = {
  readonly headlinePrefix: string;
  readonly headlineAccent: string;
  readonly headlineSuffix: string;
  readonly lede: string;
  readonly ctaLabel: string;
  readonly ctaHref: string;
  readonly onCtaClick?: () => void;
  readonly cases: ReadonlyArray<ComparisonCase>;
  readonly tabs: ReadonlyArray<TabDef>;
  readonly beforeTagLabel: string;
  readonly afterTagLabel: string;
  readonly galleryAriaLabel: string;
  readonly previousAriaLabel: string;
  readonly nextAriaLabel: string;
  readonly tabsAriaLabel: string;
  readonly headlineId: string;
};

/**
 * Interactive phone-based before/after comparison slider with a 3-tab strip.
 *
 * Owns the full mocha hero band (copy column + phone + tabs strip) because
 * the slider, tabs, and pagination counter share state and DOM refs that
 * would be awkward to lift through context. The hero copy is rendered here
 * (still server-rendered in initial HTML through Next.js SSR of client
 * components) so the headline `<h1>` is part of the first paint.
 *
 * Auto-cycle is one continuous loop: repeated 0↔100 sweeps per case, then a
 * quick crossfade into the next pair. The loop does not start until the
 * mocha band intersects the viewport (initial load). Animation is driven by
 * direct DOM ref manipulation
 * inside `requestAnimationFrame`; React state is reserved for the active
 * tab index (which drives the visible tab class and counter render).
 *
 * `before-bg` and `after-bg` are both sized in pixels to the comparison
 * container's full width (see `syncWidth`). Without that, `after-bg` would
 * inherit its 50%-width wrapper and visibly shrink relative to `before-bg`,
 * which is what caused the misaligned-faces bug in earlier revisions.
 */
export default function PhoneComparisonSlider({
  headlinePrefix,
  headlineAccent,
  headlineSuffix,
  lede,
  ctaLabel,
  ctaHref,
  onCtaClick,
  cases,
  tabs,
  beforeTagLabel,
  afterTagLabel,
  galleryAriaLabel,
  previousAriaLabel,
  nextAriaLabel,
  tabsAriaLabel,
  headlineId,
}: PhoneComparisonSliderProps) {
  if (cases.length !== tabs.length) {
    throw new Error(
      `PhoneComparisonSlider: cases.length (${cases.length}) must equal tabs.length (${tabs.length}).`,
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);
  // Mirrors `state.isDragging` for the JSX — used to freeze the
  // `.alt-tab__progress-fill` CSS animation while the user is holding
  // the slider handle so the tab progress dial doesn't keep ticking
  // forward while the photo comparison is paused.
  const [isAutoPaused, setIsAutoPaused] = useState(false);

  const sectionRef = useRef<HTMLElement | null>(null);
  const phoneWrapRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const beforeBgRef = useRef<HTMLDivElement | null>(null);
  const afterBgRef = useRef<HTMLDivElement | null>(null);
  const beforeTagRef = useRef<HTMLDivElement | null>(null);
  const afterTagRef = useRef<HTMLDivElement | null>(null);
  const wipeRef = useRef<HTMLDivElement | null>(null);

  const stateRef = useRef({
    currentSet: 0,
    isDragging: false,
    startX: 0,
    startY: 0,
    scrollIntent: null as "horizontal" | "vertical" | null,
    animFrameId: null as number | null,
    autoTimeoutId: null as ReturnType<typeof setTimeout> | null,
    slideFadeTimeoutId: null as ReturnType<typeof setTimeout> | null,
    wipeAnimId: null as number | null,
    // Counts completed swoop legs on the current slide. When it hits
    // `SWOOPS_PER_SLIDE` the auto-cycle hands off to `swapCase()` and
    // advances to the next case, then resets to 0 for the new slide.
    swoopCount: 0,
    // The end position the auto-loop is currently animating toward (0 or
    // 100). Tracked across slide changes so tab navigation can resume
    // can resume from wherever the divider is, in the same direction it
    // was already moving — keeping the whole gallery on one shared loop.
    autoTo: 0 as 0 | 100,
    // For direction-based tag visibility: track the last slider position so
    // each setSlider() call can decide which side is being revealed, and a
    // short idle timer to fade both tags out when motion stops.
    lastPct: 50,
    tagIdleTimerId: null as ReturnType<typeof setTimeout> | null,
  });

  useEffect(() => {
    const section = sectionRef.current;
    const phoneWrap = phoneWrapRef.current;
    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    const handle = handleRef.current;
    const beforeBg = beforeBgRef.current;
    const afterBg = afterBgRef.current;
    const beforeTag = beforeTagRef.current;
    const afterTag = afterTagRef.current;
    const wipe = wipeRef.current;

    if (
      !section ||
      !phoneWrap ||
      !container ||
      !wrapper ||
      !handle ||
      !beforeBg ||
      !afterBg ||
      !beforeTag ||
      !afterTag ||
      !wipe
    ) {
      throw new Error(
        "PhoneComparisonSlider: required slider DOM nodes were not mounted; the component cannot run.",
      );
    }

    const state = stateRef.current;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const easeInOut = (t: number): number =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    /**
     * Pin `after-bg` to the comparison container's pixel width so the after
     * image is rendered at the same size as `before-bg` (which inherits the
     * full container via inset:0). Without this, `after-bg`'s `width:100%`
     * resolves against its 50%-wide clipping wrapper and the after photo
     * appears horizontally compressed relative to the before — the
     * misalignment you can see if `syncWidth` is missing. Port of the
     * standalone alt prototype's `syncWidth()` (called on mount + resize).
     */
    const syncAfterBgWidth = (): void => {
      const w = container.offsetWidth;
      afterBg.style.width = `${w}px`;
    };

    // Anything smaller than this in a single setSlider call is treated as
    // jitter (no direction change, no tag flip). Slightly bigger than the
    // sub-pixel deltas the auto-cycle produces at its slowest ease curve.
    const MOTION_THRESHOLD_PCT = 0.15;
    // How long after the most recent movement before both tags begin
    // fading back out. Paired with the CSS `transition: opacity 0.7s
    // ease-out 0.18s` — the JS holds the visible state for this long,
    // then the CSS plays the full fade including its leading delay.
    const TAG_IDLE_FADE_MS = 520;

    const setSlider = (pctIn: number): void => {
      const pct = Math.max(0, Math.min(100, pctIn));
      wrapper.style.width = `${pct}%`;
      handle.style.left = `${pct}%`;

      // Direction-based tag visibility: only one tag is ever visible at a
      // time, and only while the slider is actually moving. Moving right
      // (pct increasing) reveals more of the AFTER side, so show AFTER;
      // moving left reveals BEFORE. When motion stops, fade both out.
      const delta = pct - state.lastPct;
      if (Math.abs(delta) >= MOTION_THRESHOLD_PCT) {
        if (delta > 0) {
          afterTag.style.opacity = "1";
          beforeTag.style.opacity = "0";
        } else {
          beforeTag.style.opacity = "1";
          afterTag.style.opacity = "0";
        }
        state.lastPct = pct;
        if (state.tagIdleTimerId !== null) {
          clearTimeout(state.tagIdleTimerId);
        }
        state.tagIdleTimerId = setTimeout(() => {
          beforeTag.style.opacity = "0";
          afterTag.style.opacity = "0";
          state.tagIdleTimerId = null;
        }, TAG_IDLE_FADE_MS);
      }
    };

    const updateSlider = (clientX: number): void => {
      const rect = container.getBoundingClientRect();
      const pct = ((clientX - rect.left) / rect.width) * 100;
      setSlider(pct);
    };

    const animateTo = (from: number, to: number, duration: number, onDone?: () => void): void => {
      if (state.animFrameId !== null) {
        cancelAnimationFrame(state.animFrameId);
      }
      const start = performance.now();
      const step = (now: number): void => {
        if (state.isDragging) return;
        const elapsed = now - start;
        const t = Math.min(elapsed / duration, 1);
        setSlider(from + (to - from) * easeInOut(t));
        if (t < 1) {
          state.animFrameId = requestAnimationFrame(step);
        } else {
          state.animFrameId = null;
          if (onDone) onDone();
        }
      };
      state.animFrameId = requestAnimationFrame(step);
    };

    /*
     * Decorative wipe glow. Fired only during the AFTER reveal leg of the
     * auto-cycle (left→right swoop, 0 % → 100 %) so the user reads it as
     * "the AFTER image is being uncovered". The wipe is a soft champagne
     * gradient that slides in from the left and fades out once it passes
     * the 80 % mark, leaving the comparison divider as the only edge.
     */
    const runWipe = (fromPct: number, toPct: number, duration: number): void => {
      if (state.wipeAnimId !== null) {
        cancelAnimationFrame(state.wipeAnimId);
      }
      const start = performance.now();
      wipe.style.opacity = "1";
      const step = (now: number): void => {
        const t = Math.min((now - start) / duration, 1);
        const ease = easeInOut(t);
        const pct = fromPct + (toPct - fromPct) * ease;
        wipe.style.width = `${pct}%`;
        const fadeOut = toPct > 80 ? Math.max(0, 1 - (pct - 80) / 20) : 1;
        wipe.style.opacity = String(fadeOut);
        if (t < 1) {
          state.wipeAnimId = requestAnimationFrame(step);
        } else {
          wipe.style.opacity = "0";
          wipe.style.width = "0%";
          state.wipeAnimId = null;
        }
      };
      state.wipeAnimId = requestAnimationFrame(step);
    };

    const stopAuto = (): void => {
      if (state.animFrameId !== null) {
        cancelAnimationFrame(state.animFrameId);
        state.animFrameId = null;
      }
      if (state.autoTimeoutId !== null) {
        clearTimeout(state.autoTimeoutId);
        state.autoTimeoutId = null;
      }
      if (state.slideFadeTimeoutId !== null) {
        clearTimeout(state.slideFadeTimeoutId);
        state.slideFadeTimeoutId = null;
      }
      container.classList.remove("is-slide-fading");
      // The wipe runs on its own RAF independent of the slider animation;
      // killing only the slider would let the gold glow keep sweeping
      // across after the user grabbed the divider or hit a tab, which
      // reads as "the AFTER fade fired when it shouldn't have".
      if (state.wipeAnimId !== null) {
        cancelAnimationFrame(state.wipeAnimId);
        state.wipeAnimId = null;
      }
      wipe.style.opacity = "0";
      wipe.style.width = "0%";
    };

    const resumeAuto = (): void => {
      stopAuto();
      // 3.5 s idle after a slider-handle drag release before the auto
      // loop resumes. Short enough that the gallery doesn't feel
      // "stuck" after a quick peek, long enough that the divider
      // doesn't jerk out from under the user the instant they let go.
      state.autoTimeoutId = setTimeout(runAutoSequence, 3500);
    };

    /*
     * Toggle the snappy "manual" transition on both tags. The default
     * 0.7 s ease-out + 0.18 s lead-in is intentional for the auto cycle
     * (badge feels considered as the slider drifts), but it lags
     * noticeably under a finger drag — flagging `is-manual` flips the
     * CSS rule to a near-instant 0.12 s fade with no delay so the tags
     * track manual motion in real time.
     */
    const setManualMode = (on: boolean): void => {
      const op = on ? "add" : "remove";
      beforeTag.classList[op]("is-manual");
      afterTag.classList[op]("is-manual");
    };

    const setCaseImages = (index: number): void => {
      const s = cases[index];
      if (!s) {
        throw new Error(
          `PhoneComparisonSlider: no comparison case at index ${index}; cases length is ${cases.length}.`,
        );
      }
      beforeBg.style.backgroundImage = `url('${s.before}')`;
      afterBg.style.backgroundImage = `url('${s.after}')`;
    };

    const swapCase = (index: number, onDone?: () => void, animate = true): void => {
      if (index === state.currentSet && !onDone) {
        return;
      }

      const applySwap = (): void => {
        state.currentSet = index;
        state.swoopCount = 0;
        setCaseImages(index);
        setActiveIndex(index);
        syncAfterBgWidth();
        onDone?.();
      };

      if (state.slideFadeTimeoutId !== null) {
        clearTimeout(state.slideFadeTimeoutId);
        state.slideFadeTimeoutId = null;
      }
      container.classList.remove("is-slide-fading");

      if (reduceMotion || !animate) {
        applySwap();
        return;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          container.classList.add("is-slide-fading");
          state.slideFadeTimeoutId = setTimeout(() => {
            state.slideFadeTimeoutId = null;
            applySwap();
            requestAnimationFrame(() => {
              container.classList.remove("is-slide-fading");
            });
          }, SLIDE_FADE_MS);
        });
      });
    };

    /*
     * One continuous shared loop across every slide:
     *   - swoop from `lastPct` to `state.autoTo` (the next 0/100 target)
     *   - on each leg-complete: flip `autoTo`, bump `swoopCount`
     *   - every `AUTO_SWOOPS_PER_SLIDE` legs: hand off to `swapCase()` with
     *     crossfade, then resume the loop on the same divider trajectory.
     *
     * Manual tab / arrow clicks swap instantly (no crossfade) without
     * stopping the loop — same divider position and direction.
     */
    function autoLoop(): void {
      if (state.isDragging) {
        state.autoTimeoutId = setTimeout(autoLoop, 500);
        return;
      }
      setManualMode(false);
      setIsAutoPaused(false);
      const from = state.lastPct;
      const to = state.autoTo;
      if (Math.abs(from - to) < 0.5) {
        // Already at the target end. Flip direction and recurse so the
        // loop never stalls (can happen right after the divider rests
        // at 0 or 100 across a slide swap).
        state.autoTo = to === 0 ? 100 : 0;
        autoLoop();
        return;
      }
      const distance = Math.abs(to - from);
      const duration = Math.max(800, AUTO_SWOOP_DURATION_MS * (distance / 100));
      if (to === 100) {
        runWipe(from, 100, duration);
      }
      animateTo(from, to, duration, () => {
        if (state.isDragging) return;
        state.swoopCount += 1;
        state.autoTo = to === 0 ? 100 : 0;
        if (state.swoopCount >= AUTO_SWOOPS_PER_SLIDE) {
          const nextIndex = (state.currentSet + 1) % cases.length;
          swapCase(nextIndex, autoLoop, true);
          return;
        }
        autoLoop();
      });
    }

    // Kept under the original name because several event handlers below
    // capture it by reference; it now just delegates to autoLoop().
    function runAutoSequence(): void {
      autoLoop();
    }

    // Mouse drag
    const onMouseDown = (e: MouseEvent): void => {
      stopAuto();
      setManualMode(true);
      setIsAutoPaused(true);
      state.isDragging = true;
      updateSlider(e.clientX);
    };
    const onWindowMouseMove = (e: MouseEvent): void => {
      if (state.isDragging) updateSlider(e.clientX);
    };
    const onWindowMouseUp = (): void => {
      if (state.isDragging) {
        state.isDragging = false;
        resumeAuto();
      }
    };

    // Touch — with horizontal-vs-vertical intent detection so vertical page
    // scroll never gets hijacked by an accidental finger on the slider.
    const onTouchStart = (e: TouchEvent): void => {
      const touch = e.touches[0];
      if (!touch) return;
      state.scrollIntent = null;
      state.startX = touch.clientX;
      state.startY = touch.clientY;
    };
    const onWindowTouchMove = (e: TouchEvent): void => {
      const touch = e.touches[0];
      if (!touch) return;
      const cx = touch.clientX;
      const cy = touch.clientY;
      if (state.scrollIntent === null) {
        const dx = Math.abs(cx - state.startX);
        const dy = Math.abs(cy - state.startY);
        if (dy > dx) {
          state.scrollIntent = "vertical";
          return;
        } else if (dx > 4) {
          const rect = container.getBoundingClientRect();
          if (cy >= rect.top && cy <= rect.bottom) {
            state.scrollIntent = "horizontal";
            stopAuto();
            setManualMode(true);
            setIsAutoPaused(true);
            state.isDragging = true;
          } else {
            state.scrollIntent = "vertical";
            return;
          }
        } else {
          return;
        }
      }
      if (state.scrollIntent === "horizontal") {
        if (e.cancelable) e.preventDefault();
        updateSlider(cx);
      }
    };
    const onWindowTouchEnd = (): void => {
      if (state.isDragging) {
        state.isDragging = false;
        resumeAuto();
      }
      state.scrollIntent = null;
    };

    container.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onWindowMouseMove);
    window.addEventListener("mouseup", onWindowMouseUp);
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onWindowTouchMove, { passive: false });
    window.addEventListener("touchend", onWindowTouchEnd);
    window.addEventListener("resize", syncAfterBgWidth);

    // Expose tab/arrow handlers via the container element so the React JSX
    // below can call them without re-creating the long-lived closure chain.
    (container as HTMLDivElement & { __changeGallery?: (i: number) => void }).__changeGallery = (
      i: number,
    ) => swapCase(i, undefined, false);
    (container as HTMLDivElement & { __stepGallery?: (d: number) => void }).__stepGallery = (
      d: number,
    ) => {
      const next = (state.currentSet + d + cases.length) % cases.length;
      swapCase(next, undefined, false);
    };

    let autoSequenceStarted = false;
    let phoneRevealed = false;

    const startAutoWhenVisible = (): void => {
      if (autoSequenceStarted || reduceMotion) {
        return;
      }
      autoSequenceStarted = true;
      const delayAfterReveal = phoneRevealed ? PHONE_REVEAL_TRANSITION_MS : 0;
      state.autoTimeoutId = setTimeout(runAutoSequence, delayAfterReveal + AUTO_START_DELAY_MS);
    };

    const revealPhone = (): void => {
      if (phoneRevealed) {
        return;
      }
      phoneRevealed = true;
      phoneWrap.classList.add("is-revealed");
    };

    const onPhoneInView = (): void => {
      revealPhone();
      startAutoWhenVisible();
      phoneInViewObserver.disconnect();
    };

    const phoneInViewObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || entry.intersectionRatio < PHONE_IN_VIEW_THRESHOLD) {
          return;
        }
        onPhoneInView();
      },
      { threshold: [0, PHONE_IN_VIEW_THRESHOLD], rootMargin: "0px 0px -12% 0px" },
    );
    phoneInViewObserver.observe(phoneWrap);

    preloadComparisonCaseImages(cases);

    // Initial paint: load case 0, sync after-bg width, snap to 50%.
    setCaseImages(0);
    syncAfterBgWidth();
    setSlider(50);

    return () => {
      phoneInViewObserver.disconnect();
      stopAuto();
      if (state.tagIdleTimerId !== null) {
        clearTimeout(state.tagIdleTimerId);
        state.tagIdleTimerId = null;
      }
      container.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onWindowMouseMove);
      window.removeEventListener("mouseup", onWindowMouseUp);
      container.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onWindowTouchMove);
      window.removeEventListener("touchend", onWindowTouchEnd);
      window.removeEventListener("resize", syncAfterBgWidth);
    };
  }, [cases]);

  const dispatchChangeGallery = (i: number): void => {
    const container = containerRef.current as
      | (HTMLDivElement & { __changeGallery?: (i: number) => void })
      | null;
    container?.__changeGallery?.(i);
  };
  const dispatchStepGallery = (delta: number): void => {
    const container = containerRef.current as
      | (HTMLDivElement & { __stepGallery?: (d: number) => void })
      | null;
    container?.__stepGallery?.(delta);
  };

  const counterText = String(activeIndex + 1).padStart(2, "0");
  const totalText = String(cases.length).padStart(2, "0");

  return (
    <div
      className="alt-demo"
      style={{ "--cycle-ms": `${AUTO_TAB_CYCLE_MS}ms` } as React.CSSProperties}
    >
      <div aria-hidden className="absolute h-0 w-0 overflow-hidden opacity-0">
        {cases.map(({ before, after }, index) => (
          <span key={before}>
            {/* eslint-disable-next-line @next/next/no-img-element -- decode all slider assets at first paint */}
            <img
              alt=""
              decoding="async"
              fetchPriority={index === 0 ? "high" : "low"}
              loading="eager"
              src={before}
            />
            {/* eslint-disable-next-line @next/next/no-img-element -- decode all slider assets at first paint */}
            <img alt="" decoding="async" fetchPriority="low" loading="eager" src={after} />
          </span>
        ))}
      </div>
      <section aria-labelledby={headlineId} className="alt-hero" ref={sectionRef}>
        <div aria-hidden="true" className="alt-hero__bg" />
        <div className="alt-hero__container">
          <div className="alt-hero__copy">
            <h2 className="alt-hero__headline type-display-lg text-balance" id={headlineId}>
              {headlinePrefix}
              <em>{headlineAccent}</em>
              {headlineSuffix}
            </h2>
            <p className="alt-hero__lede">{lede}</p>
            {ctaLabel && (onCtaClick || ctaHref) ? (
              onCtaClick ? (
                <button className="prelaunch-cta" onClick={onCtaClick} type="button">
                  <span className="prelaunch-cta-label">{ctaLabel}</span>
                </button>
              ) : (
                <Link className="prelaunch-cta" href={ctaHref}>
                  <span className="prelaunch-cta-label">{ctaLabel}</span>
                </Link>
              )
            ) : null}
          </div>
          <div className="alt-hero__phone-wrap" ref={phoneWrapRef}>
            <div className="phone" role="presentation">
              <div aria-hidden="true" className="phone__handle-overlay">
                <div className="alt-demo__handle" ref={handleRef}>
                  <div className="alt-demo__handle-knob">
                    <svg aria-hidden="true" fill="none" height="22" viewBox="0 0 20 20" width="22">
                      <path
                        d="M7 4L3 10L7 16M13 4L17 10L13 16"
                        stroke="#281D1A"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="phone__screen">
                <div className="phone__slider-wrap">
                  <div className="alt-demo__comparison" ref={containerRef}>
                    <div className="alt-demo__before-bg" ref={beforeBgRef} />
                    <div className="alt-demo__after-wrapper" ref={wrapperRef}>
                      <div className="alt-demo__after-bg" ref={afterBgRef} />
                    </div>
                    <div className="alt-demo__wipe" ref={wipeRef} />
                  </div>
                </div>
                <div aria-label={galleryAriaLabel} className="alt-demo__dark-nav" role="group">
                  <button
                    aria-label={previousAriaLabel}
                    className="alt-demo__dark-nav-arrow"
                    onClick={() => dispatchStepGallery(-1)}
                    type="button"
                  >
                    <svg aria-hidden="true" fill="none" height="12" viewBox="0 0 12 12" width="12">
                      <path
                        d="M7.5 3L4.5 6L7.5 9"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.4"
                      />
                    </svg>
                  </button>
                  <span aria-live="polite" className="alt-demo__dark-nav-counter">
                    <strong>{counterText}</strong> / {totalText}
                  </span>
                  <button
                    aria-label={nextAriaLabel}
                    className="alt-demo__dark-nav-arrow"
                    onClick={() => dispatchStepGallery(1)}
                    type="button"
                  >
                    <svg aria-hidden="true" fill="none" height="12" viewBox="0 0 12 12" width="12">
                      <path
                        d="M4.5 3L7.5 6L4.5 9"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.4"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div aria-hidden="true" className="phone__chassis" />
              {/*
               * Tags live OUTSIDE the screen (siblings of `.phone__chassis`
               * rather than children of `.alt-demo__comparison`) so they can
               * hover at the top of the phone and overflow the screen
               * clipping box. Opacity is driven by the slider position via
               * direct ref writes in setSlider().
               */}
              <div className="alt-demo__tag alt-demo__tag--before" ref={beforeTagRef}>
                {beforeTagLabel}
              </div>
              <div className="alt-demo__tag alt-demo__tag--after" ref={afterTagRef}>
                {afterTagLabel}
              </div>
            </div>
          </div>
        </div>
      </section>
      <div
        aria-label={tabsAriaLabel}
        className={`alt-tabs${isAutoPaused ? " is-paused" : ""}`}
        role="group"
      >
        <div className="alt-tabs__inner">
          {tabs.map((tab, i) => {
            const isActive = i === activeIndex;
            const Icon = TAB_ICONS[tab.id];
            return (
              <button
                aria-pressed={isActive}
                className={`alt-tab${isActive ? " is-active" : ""}`}
                key={tab.id}
                onClick={() => dispatchChangeGallery(i)}
                type="button"
              >
                {Icon ? <Icon className="alt-tab__icon" /> : null}
                <span className="alt-tab__label">{tab.label}</span>
                <span aria-hidden="true" className="alt-tab__progress">
                  <span
                    className="alt-tab__progress-fill"
                    // Remount the fill on every activeIndex change so the
                    // CSS animation restarts cleanly — mirrors the alt
                    // prototype's `void offsetWidth` reflow trick.
                    key={`fill-${activeIndex}`}
                  />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
